import { Colors } from "../../../utils/colors";
import { useCallback, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getAccountState } from "../../../redux/slices/accountSlice";
import { TabPanel } from "../../FullEventSubComponents/TabPanel";
import StandardListContainer from "../../Containers/StandardListContainer";
import SearchBoxContainer from "../../Containers/SearchBoxContainer";
import ListTabs from "../../DropdownsAndTabs/ListTabs";
import FlatlistLoadMore from "../../FlatlistLoadMore";
import {
  PlanTypeV4,
  AccountData,
  FollowerMembershipState,
  FollowerStatus,
  MembershipState,
} from "@markit/common.types";
import { useLoadUserFollowList } from "../../../hooks/useLoadUserFollowList";
import filter from "lodash.filter";
import EmptyStateButton from "../../Buttons/EmptyStateButton";
import { Icon } from "@iconify/react";
import { EmptyStateFlatlist } from "../../EmptyStates/EmptyStateFlatlist";
import ProfileFollowerMembershipItem, {
  MEMBERSHIP_USER_FLEX_SECTIONS,
} from "../../DisplayItem/ProfileItem/ProfileFollowerMembershipItem";
import FullProfilePreviewModal from "../../FollowerProfile/FullProfilePreviewModal";
import ShareProfileLinkModal from "../CreatorProfile/ShareProfileLinkModal";
import useAsyncEffect from "../../../hooks/useAsyncEffect";
import { hasSubscription } from "@markit/common.utils";
import { notificationActions } from "../../../redux/slices/notificationSlice";
import EmptyStateButtonSubscription from "../../Buttons/EmptyState/EmptyStateButtonSubscription";

export type MembershipMembersProps = {
  tabValue: number;
  setTabValue: (tabValue: number) => void;
  listValue: number;
  setListValue: (listValue: number) => void;
};

const MembershipMembers = (props: MembershipMembersProps) => {
  const { tabValue, setTabValue, listValue, setListValue } = props;
  const { accountData, followersData } = useSelector(getAccountState).account;
  const dispatch = useDispatch();
  const [searchTerm, setSearchTerm] = useState("");
  const [profilePreviewSelected, setProfilePreviewSelected] =
    useState<AccountData>();
  const [shareModal, setShareModal] = useState(false);

  const membershipsEnabled = useMemo(
    () =>
      accountData.membership &&
      accountData.membership.state === MembershipState.ACTIVE &&
      accountData.membership.membershipPlans,
    [accountData.membership]
  );

  const subscribedFollowers = useMemo(
    () =>
      followersData.filter(
        (follower) => follower.status === FollowerStatus.SUBSCRIBED
      ),
    [followersData]
  );

  const numFreeMembers = useMemo(
    () =>
      subscribedFollowers.filter(
        (follower) => follower.membershipPlanId === "free"
      ).length,
    [subscribedFollowers]
  );

  const numPaidMembers = useMemo(
    () =>
      subscribedFollowers.filter(
        (follower) =>
          follower.membershipPlanId !== "free" &&
          follower.membershipState === FollowerMembershipState.ACTIVE
      ).length,
    [subscribedFollowers]
  );

  const numPaymentFailedMembers = useMemo(
    () =>
      subscribedFollowers.filter(
        (follower) =>
          follower.membershipState === FollowerMembershipState.PAYMENT_FAILED
      ).length,
    [subscribedFollowers]
  );

  const tabLabels = useMemo(
    () =>
      membershipsEnabled
        ? numPaymentFailedMembers > 0
          ? ["Paid", "Free", "Payment Failed"]
          : ["Paid", "Free"]
        : [],
    [membershipsEnabled, numPaymentFailedMembers]
  );

  const {
    fetchedUserData: freeUserData,
    fetchedFollowerData: freeFollowerData,
    loadMoreUsers: loadMoreFreeUsers,
    loadUsers: loadFreeUsers,
    loadSearchResultsThrottled: loadFreeSearchResultsThrottled,
    isLoading: isFreeLoading,
    isFinished: isFreeFinished,
  } = useLoadUserFollowList({
    userId: accountData.uid,
    followListType: "Followers",
    followerStatus: FollowerStatus.SUBSCRIBED,
    membershipState: FollowerMembershipState.INACTIVE,
    windowSize: 30,
  });

  const {
    fetchedUserData: paidUserData,
    fetchedFollowerData: paidFollowerData,
    loadMoreUsers: loadMorePaidUsers,
    loadUsers: loadPaidUsers,
    loadSearchResultsThrottled: loadPaidSearchResultsThrottled,
    isLoading: isPaidLoading,
    isFinished: isPaidFinished,
  } = useLoadUserFollowList({
    userId: accountData.uid,
    followListType: "Followers",
    followerStatus: FollowerStatus.SUBSCRIBED,
    membershipState: FollowerMembershipState.ACTIVE,
    windowSize: 30,
  });

  const {
    fetchedUserData: paymentFailedUserData,
    fetchedFollowerData: paymentFailedFollowerData,
    loadMoreUsers: loadMorePaymentFailedUsers,
    loadUsers: loadPaymentFailedUsers,
    loadSearchResultsThrottled: loadPaymentFailedSearchResultsThrottled,
    isLoading: isPaymentFailedLoading,
    isFinished: isPaymentFailedFinished,
  } = useLoadUserFollowList({
    userId: accountData.uid,
    followListType: "Followers",
    followerStatus: FollowerStatus.SUBSCRIBED,
    membershipState: FollowerMembershipState.PAYMENT_FAILED,
    windowSize: 30,
  });

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useAsyncEffect(async () => {
    if (membershipsEnabled) {
      await Promise.all([
        loadFreeUsers(),
        loadPaidUsers(),
        loadPaymentFailedUsers(),
      ]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [membershipsEnabled]);

  const hasSub = useMemo(() => hasSubscription(accountData), [accountData]);

  const emptyStateOnPress = useCallback(() => {
    if (hasSub) {
      setTabValue(2);
    } else {
      dispatch(
        notificationActions.setMarkitPlusModal(PlanTypeV4.ESSENTIAL.name)
      );
    }
  }, [hasSub, setTabValue, dispatch]);

  const contains = useCallback((user: AccountData, query: string) => {
    return user.fullName.toLowerCase().includes(query);
  }, []);

  const handleSearch = (text: string) => {
    const lowerCaseText = text.toLowerCase();
    if (text !== "") {
      if (listValue === 0) {
        loadPaidSearchResultsThrottled(lowerCaseText);
      } else if (listValue === 1) {
        loadFreeSearchResultsThrottled(lowerCaseText);
      } else {
        loadPaymentFailedSearchResultsThrottled(lowerCaseText);
      }
    }
    setSearchTerm(lowerCaseText);
  };

  const fetchedUserData = useMemo(() => {
    if (listValue === 0) {
      return paidUserData;
    } else if (listValue === 1) {
      return freeUserData;
    } else {
      return paymentFailedUserData;
    }
  }, [listValue, paidUserData, freeUserData, paymentFailedUserData]);

  const isLoading = useMemo(() => {
    if (listValue === 0) {
      return isPaidLoading;
    } else if (listValue === 1) {
      return isFreeLoading;
    } else {
      return isPaymentFailedLoading;
    }
  }, [listValue, isFreeLoading, isPaidLoading, isPaymentFailedLoading]);

  const isFinished = useMemo(() => {
    if (listValue === 0) {
      return isPaidFinished;
    } else if (listValue === 1) {
      return isFreeFinished;
    } else {
      return isPaymentFailedFinished;
    }
  }, [listValue, isFreeFinished, isPaidFinished, isPaymentFailedFinished]);

  const loadMoreUsers = useMemo(() => {
    if (listValue === 0) {
      return loadMorePaidUsers;
    } else if (listValue === 1) {
      return loadMoreFreeUsers;
    } else {
      return loadMorePaymentFailedUsers;
    }
  }, [
    listValue,
    loadMoreFreeUsers,
    loadMorePaidUsers,
    loadMorePaymentFailedUsers,
  ]);

  const currentItemsToShow = useMemo(() => {
    let userList: AccountData[] = fetchedUserData;
    if (searchTerm !== "") {
      userList = filter(userList, (user: AccountData) => {
        return contains(user, searchTerm);
      });
    }
    return userList;
  }, [fetchedUserData, searchTerm, contains]);

  const currentFollowerDataToShow = useMemo(
    () =>
      listValue === 0
        ? paidFollowerData
        : listValue === 1
          ? freeFollowerData
          : paymentFailedFollowerData,
    [listValue, paidFollowerData, freeFollowerData, paymentFailedFollowerData]
  );

  const renderEmptyView = useMemo(() => {
    return (
      <EmptyStateFlatlist
        searchTerm={searchTerm}
        isLoading={isLoading}
        containerStyles={{ paddingTop: 100 }}
        nonSearchEmptyView={
          <EmptyStateButton
            title={"No Members"}
            description={
              "Share your profile to grow your subscriber lists and generate monthly revenue."
            }
            icon={
              <Icon
                icon="ion:people"
                height={64}
                style={{ color: Colors.GRAY1 }}
              />
            }
            iconBox={84}
            containerStyles={{ paddingTop: 100 }}
            btnText="Share Profile"
            onPress={() => setShareModal(true)}
          />
        }
      />
    );
  }, [isLoading, searchTerm]);

  const renderItem = useCallback(
    (item: AccountData) => {
      return (
        <div key={item.uid}>
          <ProfileFollowerMembershipItem
            item={item}
            setProfilePreviewSelected={setProfilePreviewSelected}
            fetchedFollowerData={currentFollowerDataToShow}
          />
        </div>
      );
    },
    [currentFollowerDataToShow]
  );

  return (
    <TabPanel value={tabValue} index={0}>
      {membershipsEnabled && hasSub ? (
        <StandardListContainer
          searchComp={
            <div className="ColumnNormal" style={{ gap: 14 }}>
              <SearchBoxContainer
                placeholder="Search"
                onChange={(e) => handleSearch(e.target.value)}
                containerStyles={{ marginTop: 0, width: 394 }}
              />
              <ListTabs
                tabLabels={tabLabels}
                tabNumbers={
                  numPaymentFailedMembers > 0
                    ? [numPaidMembers, numFreeMembers, numPaymentFailedMembers]
                    : [numPaidMembers, numFreeMembers]
                }
                selectedValue={listValue}
                onChange={setListValue}
                altColor={Colors.BLACK}
              />
            </div>
          }
          gridTable={{
            flexSections: MEMBERSHIP_USER_FLEX_SECTIONS,
            sectionTitles: [
              "Name",
              "Membership Plan",
              "Promo",
              "Subscribe Date",
            ],
          }}
          listComp={
            <FlatlistLoadMore
              fullList={fetchedUserData}
              currentList={currentItemsToShow}
              renderItem={(item: AccountData) => renderItem(item)}
              renderWhenEmpty={renderEmptyView}
              isLoadingMore={isLoading}
              showLoadMore={searchTerm.trim() === "" && !isFinished}
              loadMoreOnPress={loadMoreUsers}
            />
          }
        />
      ) : !hasSub ? (
        <EmptyStateButtonSubscription
          planType={PlanTypeV4.ESSENTIAL}
          iconName={"ion:ribbon"}
          description="Start earning by creating paid subscriber plans"
          btnText="Get Essential Plan"
        />
      ) : (
        <div className="Centering" style={{ paddingBlock: 120 }}>
          <EmptyStateButton
            title="Enable Paid Plans to Start Earning"
            description="Your free and paid plans will show up here once you connect to Stripe and enable paid plans."
            icon={
              <Icon
                icon={"ion:ribbon"}
                height={50}
                style={{ color: Colors.GREEN2 }}
              />
            }
            iconBox={80}
            iconBackgroundColor={Colors.WHITE1}
            btnText="Enable Paid Plans"
            onPress={emptyStateOnPress}
            buttonContainerStyles={{ backgroundColor: Colors.GREEN2 }}
          />
        </div>
      )}
      {profilePreviewSelected ? (
        <FullProfilePreviewModal
          profileSelected={profilePreviewSelected}
          setProfileSelected={setProfilePreviewSelected}
        />
      ) : null}
      {shareModal ? (
        <ShareProfileLinkModal
          username={accountData.username}
          setShareLinkModal={setShareModal}
        />
      ) : null}
    </TabPanel>
  );
};

export default MembershipMembers;
